import ArtistPremiumPerformance from "../Premium/components/ArtistPremiumPerformance";
import CommonGetInTouchBase from "../CommonGetInTouch/CommonGetInTouchBase";
import ArtistFreeGallery from "./components/ArtistFreeGallery";
import ArtistFreeAwards from "./components/ArtistFreeAwards";
import ArtistFreeStory from "./components/ArtistFreeStory";
import ArtistPreviewTags from "../utils/ArtistPreviewTags";
import ArtistFreeHero from "./components/ArtistFreeHero";
import ArtistReview from "../utils/ArtistReview";
import { Meta } from "@/layout/Meta";
import React, { useEffect, useRef, useState } from "react";
import ArtistBadges from "../utils/ArtistBadges";
import { useNavigate } from "react-router-dom";
import { CheckmarkIcon, SVGBlackComponent, SVGGoldComponent, SVGSilverComponent } from "../SVGBadges/SVGBadges";
import ArtistPastPerformances from "./components/ArtistPastPerformances";
import { mockData } from "../../mockData";

const ArtistFreeProfile = ({ isMobile }) => {
  const navigate = useNavigate();
  const profileOptions = [
    { label: "Free", url: "/preview-my-website?plan=free", banner: <CheckmarkIcon /> },
    { label: "Economy", url: "/preview-my-website?plan=economy", banner: <SVGBlackComponent /> },
    { label: "Premium", url: "/preview-my-website?plan=premium", banner: <SVGGoldComponent /> },
    { label: "Elite", url: "/preview-my-website?plan=elite", banner: <SVGSilverComponent /> },
  ];
  const [selectedProfile, setSelectedProfile] = useState("Free");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const pingColorMap: Record<string, string> = {
    Free: 'bg-gray-100',
    Economy: 'bg-gray-800',
    Premium: 'bg-yellow-400',
    Elite: 'bg-pink-500',
  };

  // Refs for main sections to track visibility
  const heroRef = useRef(null);
  const storyRef = useRef(null);
  const galleryRef = useRef(null); // Combined gallery and videos

  // State to track the current section in view
  const [currentSection, setCurrentSection] = useState<string | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle navigation visibility on scroll
  useEffect(() => {
    let lastY = window.scrollY;
    const handleScroll = () => {
      const currentY = window.scrollY;
      setIsNavVisible(currentY <= lastY);
      lastY = currentY;
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Set up Intersection Observer to update URL hash based on section visibility
  useEffect(() => {
    const options = {
      root: null, // Use viewport as root
      rootMargin: '0px',
      threshold: 0.6, // Trigger when 70% of the section is visible
    };

    const observer = new IntersectionObserver((entries) => {
      let maxRatio = 0;
      let currentSectionId: string | null = null;
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            currentSectionId = entry.target.id;
          }
        }
      });
      // Update state and URL if a main section is in view
      if (currentSectionId && ['hero', 'story', 'gallery'].includes(currentSectionId)) {
        setCurrentSection(currentSectionId);
        // Add hash to URL reflecting the current section
        window.history.replaceState(null, '', `#${currentSectionId}`);
      } else {
        setCurrentSection(null);
        // Remove hash from URL when no main section is in view
        window.history.replaceState(null, '', window.location.pathname);
      }
    }, options);

    // Observe main sections
    if (heroRef.current) observer.observe(heroRef.current);
    if (storyRef.current) observer.observe(storyRef.current);
    if (galleryRef.current) observer.observe(galleryRef.current);

    // Cleanup observers on unmount
    return () => {
      if (heroRef.current) observer.unobserve(heroRef.current);
      if (storyRef.current) observer.unobserve(storyRef.current);
      if (galleryRef.current) observer.unobserve(galleryRef.current);
    };
  }, []);

  return (
    <div className="pt-10 max-w-screen-xxl mx-auto overflow-hidden">
      {/* <div
        ref={dropdownRef}
        className={
          `fixed right-4 md:right-20 z-[20] transition-transform duration-200 ` +
          (isNavVisible
            ? "top-20 md:top-40 translate-y-0"
            : "top-0 -translate-y-full")
        }
      >
        <button
          onClick={() => setIsDropdownOpen((prev) => !prev)}
          className="
      relative           
      overflow-visible   
      bg-[#080810] 
      border border-gray-700
      rounded-full px-5 py-2
      shadow-sm
      focus:outline-none focus:ring-2 focus:ring-blue-500
      transition duration-200
      flex items-center gap-1
    "
        >
          <div className="absolute top-1 right-2 flex items-center justify-center">
            <span
              className={`absolute inline-flex h-4 w-4 rounded-full ${pingColorMap[selectedProfile]} opacity-75 animate-bubble`}
            />
            <span
              className={`relative inline-flex rounded-full h-0 w-0 ${pingColorMap[selectedProfile]}`}
            />
          </div>
          {isDropdownOpen && (
            <div className="">
              <div
                key={selectedProfile}
                className={` rounded-full  ${{
                  Free: 'bg-gray-100',
                  Economy: 'bg-gray-800',
                  Premium: 'bg-yellow-400',
                  Elite: 'bg-pink-500',
                }[selectedProfile]
                  }`}
              />
            </div>
          )}

          <span className="relative flex items-center gap-2 h-5">
            <div className="scale-50 -mx-3">
              {profileOptions.find(opt => opt.label === selectedProfile)?.banner}
            </div>

            <span>{selectedProfile}</span>
            <svg
              className={`w-4 h-4 transform transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''
                }`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="white"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 8.25l-7.5 7.5-7.5-7.5"
              />
            </svg>
          </span>
        </button>

        {isDropdownOpen && (
          <div className="mt-2 w-fit bg-[#080810] rounded-md shadow-lg overflow-hidden animate-fade-in origin-top-left">
            {profileOptions.map((option) => {
              const isSelected = selectedProfile === option.label;
              const hoverBgMap = {
                Free: 'hover:bg-black/80 hover:text-white',
                Economy: 'hover:bg-gray-700 hover:text-white',
                Premium: 'hover:bg-yellow-500 hover:text-black',
                Elite: 'hover:bg-pink-500 hover:text-white',
              };
              const selectedBgMap = {
                Free: 'bg-black/90 text-white',
                Economy: 'bg-gray-700 text-white',
                Premium: 'bg-yellow-100 text-black',
                Elite: 'bg-pink-800 text-white',
              };
              return (
                <button
                  key={option.label}
                  onClick={() => {
                    setSelectedProfile(option.label);
                    setIsDropdownOpen(false);
                    // Navigate with hash only if a main section is in view
                    const url = currentSection ? `${option.url}#${currentSection}` : option.url;
                    navigate(url);
                  }}
                  className={`w-full text-left px-4 py-2 transition-colors flex flex-row justify-start items-center gap-1 ${isSelected
                    ? selectedBgMap[option.label]
                    : hoverBgMap[option.label]
                    }`}
                >
                  <div>{option.banner}</div>
                  <p>{option.label}</p>
                </button>
              );
            })}
          </div>
        )}
      </div> */}
      <div>
        <Meta
          title="Book / Hire CELEBRITY APPEARANCE Tom Cruise for Events in Best Prices - StarClinch"
          description="Hire CELEBRITY APPEARANCE Tom Cruise in Best Price. Contact for booking Tom Cruise for Events. Request quote → Get Call Back from Client Service Manager → Book Artist in Best Rates. Option to Pay for Booking in Easy Installments %"
        />
        <div ref={heroRef} id="hero">
          <ArtistFreeHero />
        </div>
        <div ref={storyRef} id="story">
          <ArtistFreeStory />
        </div>
        {/* <ArtistBadges /> */}
        <ArtistFreeAwards />
        <ArtistPremiumPerformance />
        <ArtistPastPerformances />
        <div ref={galleryRef} id="gallery">
          <ArtistFreeGallery />
        </div>
        <ArtistReview />
        <CommonGetInTouchBase profile={mockData} isMobile={isMobile} />
        <div className="mt-20">
          <ArtistPreviewTags maskDisplayName />
        </div>
      </div>
    </div>
  );
};

export default ArtistFreeProfile;